import { Check, Zap, Calendar, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { PRICING_PLANS } from "@/constants/pricingPlans";

const PLAN_ICONS = { weekly: Zap, monthly: Calendar, annual: Trophy };

/** Bold lead-in before first ". " (e.g. "One week." / "Stay organized.") */
function PlanBulletRichText({ text, highlighted }) {
  const idx = text.indexOf(". ");
  if (idx === -1) {
    return (
      <span
        className={cn(
          "font-semibold",
          highlighted ? "text-primary-foreground" : "text-foreground"
        )}
      >
        {text}
      </span>
    );
  }
  const head = text.slice(0, idx + 1);
  const rest = text.slice(idx + 2);
  return (
    <>
      <span
        className={cn(
          "font-semibold",
          highlighted ? "text-primary-foreground" : "text-foreground"
        )}
      >
        {head}
      </span>{" "}
      <span className={highlighted ? "text-primary-foreground/95" : undefined}>{rest}</span>
    </>
  );
}

/**
 * Three-column pricing cards (same layout as Pricing page).
 * @param {{ compact?: boolean }} props
 */
export function PricingPlansGrid({
  plans = PRICING_PLANS,
  checkoutLoading,
  subscribedPlan,
  onSelectPlan,
  compact = false,
  className,
}) {
  return (
    <div
      className={cn(
        "grid max-md:gap-4 gap-6 md:grid-cols-3 md:gap-5 lg:gap-6 items-stretch max-md:pt-1 pt-2 md:pt-3 overflow-visible lg:items-start",
        className
      )}
    >
      {plans.map((plan) => {
        const IconComponent = PLAN_ICONS[plan.icon];
        const isLoading = checkoutLoading === plan.id;
        const isCurrentPlan = subscribedPlan === plan.id;
        return (
          <div
            key={plan.id}
            className={cn(
              "relative flex flex-col overflow-visible",
              compact ? "max-md:pt-5 pt-7" : "max-md:pt-6 pt-8",
              /* Mobile: Monthly first; desktop: Weekly | Monthly (center) | Annual */
              plan.id === "monthly" && "order-1 md:order-2 md:z-10",
              plan.id === "weekly" && "order-2 md:order-1",
              plan.id === "annual" && "order-3 md:order-3",
              plan.badge && "z-10"
            )}
          >
            {plan.badge && (
              <span
                className={cn(
                  "absolute left-1/2 -translate-x-1/2 -translate-y-1/2 z-[20] inline-flex items-center rounded-full font-bold whitespace-nowrap shrink-0 max-w-[calc(100%-1rem)]",
                  compact
                    ? "max-md:top-5 top-7 max-md:px-3.5 max-md:py-1 max-md:text-xs px-4 py-2 text-sm"
                    : "max-md:top-6 top-8 max-md:px-4 max-md:py-1.5 max-md:text-sm px-5 py-2.5 text-base",
                  compact ? "gap-1" : "gap-1.5",
                  plan.highlighted
                    ? "bg-primary text-primary-foreground ring-2 ring-primary-foreground/30 shadow-md"
                    : "bg-muted text-muted-foreground"
                )}
              >
                {plan.badge}
                {plan.savePercent != null && (
                  <span className="ml-1 font-normal opacity-90">· Save {plan.savePercent}%</span>
                )}
              </span>
            )}
            {plan.savePercent != null && !plan.badge && (
              <span
                className={cn(
                  "absolute left-1/2 -translate-x-1/2 -translate-y-1/2 z-[20] inline-flex items-center rounded-full font-bold whitespace-nowrap shrink-0 bg-primary/10 text-primary border border-primary/20",
                  compact
                    ? "max-md:top-5 top-7 max-md:px-3.5 max-md:py-1 max-md:text-xs px-4 py-2 text-sm"
                    : "max-md:top-6 top-8 max-md:px-4 max-md:py-1.5 max-md:text-sm px-5 py-2.5 text-base"
                )}
              >
                Save {plan.savePercent}%
              </span>
            )}
            <div
              className={cn(
                "flex flex-1 flex-col rounded-2xl border max-md:p-4 max-md:min-h-0 text-left",
                "[transition:border-color_160ms_cubic-bezier(0.23,1,0.32,1),box-shadow_160ms_cubic-bezier(0.23,1,0.32,1)]",
                compact ? "p-5 md:p-6 md:min-h-0" : "max-md:p-5 p-8 md:p-10 md:min-h-0",
                "md:[@media(hover:hover)]:hover:shadow-md",
                plan.id === "monthly" && !compact && "md:scale-[1.03] md:-mt-1 md:shadow-2xl md:ring-2 md:ring-primary/25",
                plan.badge && (compact ? "max-md:pt-8 pt-10" : "max-md:pt-9 pt-11"),
                plan.highlighted
                  ? "border-primary bg-primary text-primary-foreground md:shadow-lg ring-2 ring-primary/20"
                  : "border-border/80 bg-card/90 shadow-none hover:border-primary/25"
              )}
              data-testid={`pricing-plan-${plan.id}`}
            >
              <div
                className={cn(
                  "flex flex-col",
                  compact ? "flex-1 mb-3" : "max-md:mb-4 mb-5"
                )}
              >
                <div className="flex items-center gap-2">
                  {IconComponent && (
                    <IconComponent
                      className={cn(
                        compact ? "h-5 w-5 shrink-0 md:h-6 md:w-6" : "max-md:h-5 max-md:w-5 h-6 w-6 shrink-0",
                        plan.highlighted ? "text-primary-foreground" : "text-primary"
                      )}
                      aria-hidden
                    />
                  )}
                  <h2
                    className={cn(
                      "font-heading font-bold",
                      compact
                        ? "text-lg md:text-2xl"
                        : "max-md:text-xl text-2xl md:text-3xl",
                      plan.highlighted ? "text-primary-foreground" : "text-primary"
                    )}
                  >
                    {plan.title} Plan
                  </h2>
                </div>
                <ul
                  className={cn(
                    "list-none pl-0",
                    compact ? "mt-3 space-y-1.5 md:mt-4 md:space-y-2" : "max-md:mt-3 max-md:space-y-2 mt-4 space-y-2.5"
                  )}
                >
                  {plan.bullets.map((text, i) => (
                    <li
                      key={i}
                      className={cn(
                        "flex items-start gap-2",
                        compact
                          ? "text-base md:text-lg"
                          : "max-md:text-base text-lg md:text-xl",
                        plan.highlighted ? "text-primary-foreground/95" : "text-muted-foreground"
                      )}
                    >
                      <Check
                        className={cn(
                          compact ? "h-4 w-4 shrink-0 mt-0.5 md:h-5 md:w-5" : "max-md:h-4 max-md:w-4 h-5 w-5 shrink-0 mt-0.5",
                          plan.highlighted ? "text-primary-foreground" : "text-primary"
                        )}
                        aria-hidden
                      />
                      <span>
                        <PlanBulletRichText text={text} highlighted={plan.highlighted} />
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div
                className={cn(
                  "flex flex-col",
                  compact ? "mt-auto gap-3 md:gap-4" : "max-md:gap-3 gap-4"
                )}
              >
                {plan.priceDisplay === "monthly_effective_weekly" ? (
                  <>
                    <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-1">
                      <span
                        className={cn(
                          "font-heading font-bold tracking-tight",
                          compact ? "text-2xl md:text-3xl" : "max-md:text-2xl text-3xl md:text-4xl",
                          plan.highlighted ? "text-primary-foreground" : "text-foreground"
                        )}
                      >
                        {plan.priceLabel}
                      </span>
                      <span
                        className={cn(
                          compact ? "text-base md:text-lg" : "max-md:text-base text-lg md:text-xl",
                          "font-semibold",
                          plan.highlighted ? "text-primary-foreground/95" : "text-muted-foreground"
                        )}
                      >
                        {plan.period}
                      </span>
                      {plan.effectiveLabel ? (
                        <span
                          className={cn(
                            compact ? "text-xs md:text-sm" : "text-sm md:text-base",
                            "font-medium",
                            plan.highlighted ? "text-primary-foreground/80" : "text-muted-foreground"
                          )}
                        >
                          ({plan.effectiveLabel})
                        </span>
                      ) : null}
                    </div>
                    {plan.priceNote && (
                      <p
                        className={cn(
                          compact ? "text-sm md:text-base" : "text-sm md:text-base",
                          "font-semibold leading-snug",
                          plan.highlighted ? "text-primary-foreground" : "text-foreground"
                        )}
                      >
                        {plan.priceNote}
                        {plan.priceNoteExtra && (
                          <span
                            className={cn(
                              "mt-0.5 block font-medium",
                              plan.highlighted
                                ? "text-primary-foreground/90"
                                : "text-foreground/90"
                            )}
                          >
                            {plan.priceNoteExtra}
                          </span>
                        )}
                      </p>
                    )}
                  </>
                ) : plan.priceDisplay === "weekly_anchor" || plan.priceDisplay === "weekly_only" ? (
                  <>
                    <div className="flex items-baseline gap-1.5 flex-wrap">
                      <span
                        className={cn(
                          "font-heading font-bold tracking-tight",
                          compact ? "text-2xl md:text-3xl" : "max-md:text-2xl text-3xl md:text-4xl",
                          plan.highlighted ? "text-primary-foreground" : "text-foreground"
                        )}
                      >
                        {plan.priceLabel}
                      </span>
                      <span
                        className={cn(
                          compact ? "text-xs md:text-sm" : "max-md:text-xs text-sm md:text-base",
                          plan.highlighted ? "text-primary-foreground/90" : "text-muted-foreground"
                        )}
                      >
                        {plan.period}
                      </span>
                    </div>
                    {plan.priceNote && (
                      <p
                        className={cn(
                          compact ? "text-xs md:text-sm" : "max-md:text-xs text-sm md:text-base",
                          plan.highlighted ? "text-primary-foreground/85" : "text-muted-foreground"
                        )}
                      >
                        {plan.priceNote}
                        {plan.priceNoteExtra && (
                          <span className="block mt-0.5 font-medium text-foreground/90">
                            {plan.priceNoteExtra}
                          </span>
                        )}
                      </p>
                    )}
                  </>
                ) : null}
                <Button
                  type="button"
                  className={cn(
                    "w-full font-semibold",
                    compact
                      ? "text-sm md:text-base"
                      : "max-md:text-sm text-base",
                    plan.highlighted
                      ? compact
                        ? "h-10 md:h-11 border-2 border-primary-foreground/30 bg-primary-foreground text-primary hover:bg-primary-foreground/90 hover:text-primary shadow-md"
                        : "max-md:h-11 h-12 max-md:text-base text-lg border-2 border-primary-foreground/30 bg-primary-foreground text-primary hover:bg-primary-foreground/90 hover:text-primary shadow-lg"
                      : compact
                        ? "h-9 md:h-10"
                        : "max-md:h-10 h-11"
                  )}
                  variant={plan.highlighted ? "default" : "outline"}
                  size="lg"
                  disabled={
                    isCurrentPlan || isLoading || (checkoutLoading != null && !isLoading)
                  }
                  onClick={() => {
                    if (!isCurrentPlan) onSelectPlan(plan.id);
                  }}
                >
                  {isCurrentPlan ? "Your Plan" : isLoading ? "Redirecting…" : plan.cta}
                </Button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
